import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { queryStringToObj } from 'utils/queryParamsURL'
import { getMasterRateList } from 'features/Enterprise/Payroll/services/payroll/masterRate'
import type {
  GetMasterRateListResponseInterface,
  MasterRateApiItemInterface,
} from 'features/Enterprise/Payroll/types'

import { setLoading, setMasterRateList, setQuery, sliceName } from './masterRateSlice'

type AxiosResponseLikeType = { data?: GetMasterRateListResponseInterface }

const parseResponseList = (
  response: AxiosResponseLikeType | GetMasterRateListResponseInterface,
): MasterRateApiItemInterface[] => {
  const body =
    (response as AxiosResponseLikeType)?.data ?? (response as GetMasterRateListResponseInterface)
  const bodyData = body?.data
  if (Array.isArray(bodyData)) return bodyData
  return []
}

const parsePagination = (
  response: AxiosResponseLikeType | GetMasterRateListResponseInterface,
): { prevCursor: string; nextCursor: string } => {
  const body =
    (response as AxiosResponseLikeType)?.data ?? (response as GetMasterRateListResponseInterface)
  const pagination = body?.pagination ?? {}
  return {
    prevCursor: pagination?.prev_cursor || '',
    nextCursor: pagination?.next_cursor || '',
  }
}

export const getMasterRateListThunk = createAsyncThunk(
  `${sliceName}/getMasterRateList`,
  async (
    payload: { role?: string; cursor?: string; page_size?: number; pageIndex?: number },
    { dispatch, getState },
  ) => {
    const {
      masterRate: { query },
    } = getState() as StoreStateType

    try {
      dispatch(setLoading(true))
      const pageSize = payload.page_size ?? query.pageSize
      const currentCursor = payload.cursor ?? ''
      const response = await getMasterRateList({
        role: payload.role || undefined,
        cursor: currentCursor || undefined,
        page_size: pageSize,
      })
      const list = parseResponseList(response)
      const { prevCursor, nextCursor } = parsePagination(response)
      const hasNext = !!nextCursor
      const pageIndex = payload.pageIndex ?? (currentCursor ? 2 : 1)

      const { router } = getState() as StoreStateType
      const locationSearch = router?.location?.search ?? ''
      const currentParams = queryStringToObj(locationSearch || '') as {
        role?: string
        cursor?: string
        page_size?: string
      }
      const isStale = (currentParams?.cursor ?? '') !== (payload.cursor ?? '')

      if (isStale) return { prevCursor, nextCursor }

      const numberOfElements = hasNext
        ? pageIndex * pageSize + 1
        : Math.max(0, pageIndex * pageSize - 1)
      dispatch(
        setQuery({ ...query, pageIndex, pageSize, prevCursor, nextCursor, numberOfElements }),
      )
      dispatch(setMasterRateList(list))
      return { prevCursor, nextCursor }
    } catch (error) {
      callErrorMsg(error)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)
